// 재고 관리 모듈 타입 정의

export interface Item {
  id: string;
  code: string;
  name: string;
  category: string;
  unit: string;
  price: number;
  cost: number;
  minStock: number;
  maxStock: number;
  description?: string;
  barcode?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ItemCategory {
  id: string;
  name: string;
  itemCount: number;
}

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  address: string;
  capacity: number;
  usedCapacity: number;
  isActive: boolean;
  manager?: string;
}

export interface StockBalance {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseName: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  unit: string;
  lastUpdated: string;
}

export type StockEntryType = 'receive' | 'issue' | 'transfer' | 'adjustment';

export const STOCK_ENTRY_TYPE_LABELS: Record<StockEntryType, string> = {
  receive: '입고',
  issue: '출고',
  transfer: '이동',
  adjustment: '조정',
};

export const STOCK_ENTRY_TYPE_COLORS: Record<StockEntryType, string> = {
  receive: 'bg-green-500',
  issue: 'bg-red-500',
  transfer: 'bg-blue-500',
  adjustment: 'bg-yellow-500',
};

export interface StockEntry {
  id: string;
  entryNo: string;
  type: StockEntryType;
  itemId: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  unit: string;
  fromWarehouseId?: string;
  fromWarehouseName?: string;
  toWarehouseId?: string;
  toWarehouseName?: string;
  reason?: string;
  reference?: string;
  status: StockEntryStatus;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
}

export type StockEntryStatus = 'draft' | 'submitted' | 'completed' | 'cancelled';

export const STOCK_ENTRY_STATUS_LABELS: Record<StockEntryStatus, string> = {
  draft: '초안',
  submitted: '제출됨',
  completed: '완료',
  cancelled: '취소',
};

// 창고 용량 계산
export function calculateWarehouseUsage(warehouse: Warehouse): number {
  return Math.round((warehouse.usedCapacity / warehouse.capacity) * 100);
}

// 재고 상태 판단
export type StockStatus = 'normal' | 'low' | 'out' | 'over';

export function getStockStatus(
  quantity: number,
  minStock: number,
  maxStock: number
): StockStatus {
  if (quantity === 0) return 'out';
  if (quantity < minStock) return 'low';
  if (quantity > maxStock) return 'over';
  return 'normal';
}

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  normal: '정상',
  low: '부족',
  out: '품절',
  over: '과잉',
};

export const STOCK_STATUS_COLORS: Record<StockStatus, string> = {
  normal: 'text-green-500',
  low: 'text-yellow-500',
  out: 'text-red-500',
  over: 'text-blue-500',
};

// 재고 이동 번호 생성
export function generateStockEntryNo(type: StockEntryType): string {
  const prefix = {
    receive: 'RCV',
    issue: 'ISS',
    transfer: 'TRF',
    adjustment: 'ADJ',
  }[type];
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `${prefix}-${dateStr}-${random}`;
}
